import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import api from '@/lib/api';
import { 
  Search, Download, Calendar, Filter, Loader2, 
  Clock, UserCheck, Bus, AlertTriangle, FileText
} from 'lucide-react';

const TIPOS_RETIRADA = {
  normal: { label: 'Normal', color: 'bg-green-100 text-green-700' },
  van: { label: 'Van', color: 'bg-blue-100 text-blue-700' },
  excecao: { label: 'Exceção', color: 'bg-amber-100 text-amber-700' },
};

export default function DnaGoRetiradas() {
  const [retiradas, setRetiradas] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroTurma, setFiltroTurma] = useState('todas');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroData, setFiltroData] = useState(new Date().toISOString().split('T')[0]);
  const [stats, setStats] = useState({ total: 0, normal: 0, van: 0, excecao: 0 });

  useEffect(() => {
    loadData();
  }, [filtroData, filtroTurma, filtroTipo]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroData) {
        params.append('data_inicio', `${filtroData}T00:00:00`);
        params.append('data_fim', `${filtroData}T23:59:59`);
      }
      if (filtroTurma !== 'todas') params.append('turma_id', filtroTurma);
      if (filtroTipo !== 'todos') params.append('tipo', filtroTipo);
      params.append('limit', '500');

      const [retiradasRes, turmasRes] = await Promise.all([
        api.get(`/dnago/retiradas?${params.toString()}`),
        api.get('/class-groups')
      ]);

      const data = retiradasRes.data || [];
      setRetiradas(data);
      setTurmas(turmasRes.data || []);

      // Calcular estatísticas
      setStats({
        total: data.length,
        normal: data.filter(r => r.tipo === 'normal').length,
        van: data.filter(r => r.tipo === 'van').length,
        excecao: data.filter(r => r.tipo === 'excecao').length,
      });
    } catch (error) {
      toast.error('Erro ao carregar retiradas');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const filteredRetiradas = retiradas.filter(r =>
    r.aluno_nome?.toLowerCase().includes(search.toLowerCase()) ||
    r.autorizado_nome?.toLowerCase().includes(search.toLowerCase())
  );

  const exportToCSV = () => {
    const headers = ['Horário', 'Aluno', 'Turma', 'Autorizado', 'Tipo', 'Operador', 'Face Verificada'];
    const rows = filteredRetiradas.map(r => [
      formatTime(r.hora_retirada),
      r.aluno_nome,
      r.turma_nome,
      r.autorizado_nome,
      TIPOS_RETIRADA[r.tipo]?.label || r.tipo,
      r.operador_nome,
      r.face_verified ? 'Sim' : 'Não'
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `retiradas_${filtroData}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-7 h-7 text-purple-600" />
            Histórico de Retiradas
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Registro completo de todas as retiradas
          </p>
        </div>
        <Button variant="outline" onClick={exportToCSV} className="gap-2">
          <Download className="w-4 h-4" />
          Exportar CSV
        </Button>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700">{stats.total}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">{stats.normal}</p>
                <p className="text-xs text-gray-500">Normal</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Bus className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{stats.van}</p>
                <p className="text-xs text-gray-500">Van</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700">{stats.excecao}</p>
                <p className="text-xs text-gray-500">Exceção</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por aluno ou autorizado..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Input
                type="date"
                value={filtroData}
                onChange={(e) => setFiltroData(e.target.value)}
                className="w-40"
              />
              <Select value={filtroTurma} onValueChange={setFiltroTurma}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Turma" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as turmas</SelectItem>
                  {turmas.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="van">Van</SelectItem>
                  <SelectItem value="excecao">Exceção</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-20">Horário</TableHead>
                  <TableHead>Aluno</TableHead>
                  <TableHead className="hidden md:table-cell">Turma</TableHead>
                  <TableHead>Retirado por</TableHead>
                  <TableHead className="w-24">Tipo</TableHead>
                  <TableHead className="hidden lg:table-cell">Operador</TableHead>
                  <TableHead className="w-20 text-center hidden md:table-cell">Face</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRetiradas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      Nenhuma retirada encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRetiradas.map((retirada) => (
                    <TableRow key={retirada.id} className="hover:bg-gray-50">
                      <TableCell className="font-mono text-sm">
                        {formatTime(retirada.hora_retirada)}
                      </TableCell>
                      <TableCell className="font-medium">{retirada.aluno_nome}</TableCell>
                      <TableCell className="hidden md:table-cell text-gray-600">
                        {retirada.turma_nome}
                      </TableCell>
                      <TableCell>{retirada.autorizado_nome}</TableCell>
                      <TableCell>
                        <Badge className={TIPOS_RETIRADA[retirada.tipo]?.color || 'bg-gray-100'}>
                          {TIPOS_RETIRADA[retirada.tipo]?.label || retirada.tipo}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-gray-500 text-sm">
                        {retirada.operador_nome}
                      </TableCell>
                      <TableCell className="text-center hidden md:table-cell">
                        {retirada.face_verified ? (
                          <Badge className="bg-green-100 text-green-700">✓</Badge>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
